import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
}                           from 'react-query';
import { AxiosError }       from 'axios';
import { replace }          from 'connected-react-router';
import { callApi }          from '@utils/apiCaller';
import { Notification }     from '@components/Notification/Notification';
import { showErrorMessage } from '@utils/commonFunctions';
import store                from '@store/index';
import {
  fetchAuthInfoSucceed,
  loginSucceed,
  logoutSucceed,
}                           from '@modules/auth/authActions';
import {
  IUserInfoResponse,
  ITokenResponse,
  ILoginInitialValues,
  IResetPassPayload,
}                           from '@typings/auth';
import { getToken }         from '@modules/auth/authReducer';

const { CLIENT_ID = '' }     = process.env;
const { CLIENT_SECRET = '' } = process.env;

// #region FETCH AUTH INFO
const fetchAuthInfo = async (): Promise<void> => {
  try {
    const { data } = await callApi<IUserInfoResponse>(
      'connect/userinfo',
      'get',
      null,
      { isIdentity: true },
    );

    store.createdStore.dispatch(fetchAuthInfoSucceed(data));
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};
// #endregion

// #region LOGIN
const login = async ({ username, password }: ILoginInitialValues): Promise<void> => {
  try {
    store.createdStore.dispatch(logoutSucceed());

    const formData = new FormData();

    formData.append('client_id', CLIENT_ID);
    formData.append('client_secret', CLIENT_SECRET);
    formData.append('grant_type', 'password');
    formData.append('username', username);
    formData.append('password', password);

    const { data } = await callApi<ITokenResponse>(
      'connect/token',
      'post',
      formData,
      {
        isFormData : true,
        isIdentity : true,
      },
    );

    store.createdStore.dispatch(loginSucceed(data));

    await fetchAuthInfo();
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useLogin = (
  config: UseMutationOptions<void, AxiosError, ILoginInitialValues> = {},
): UseMutationResult<void, AxiosError, ILoginInitialValues> => useMutation(
  login,
  config,
);
// #endregion

// #region LOGOUT
export const logout = async (): Promise<void> => {
  try {
    const { getState, dispatch } = store.createdStore;

    const token    = getToken(getState());
    const formData = new FormData();

    formData.append('client_id', CLIENT_ID);
    formData.append('client_secret', CLIENT_SECRET);
    formData.append('token', token);

    await callApi(
      'connect/revocation',
      'post',
      formData,
      {
        isFormData : true,
        isIdentity : true,
      },
    );

    dispatch(logoutSucceed());

    Notification.enqueueSnackbar(
      'You were logged out.',
      'info',
    );

    dispatch(replace('/'));
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useLogout = (
  config: UseMutationOptions<void, AxiosError> = {},
): UseMutationResult<void, AxiosError, void> => useMutation(
  logout,
  config,
);
// #endregion

// #region SEND RECOVERY EMAIL
const sendRecoveryEmail = async (email: string): Promise<void> => {
  try {
    await callApi(
      'user/send-recover-pwd',
      'post',
      { email },
    );

    Notification.enqueueSnackbar(
      'A password reset email has been sent.',
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useSendRecoveryEmail = (
  config: UseMutationOptions<void, AxiosError, string> = {},
): UseMutationResult<void, AxiosError, string> => useMutation(
  sendRecoveryEmail,
  config,
);
// #endregion

// #region VALIDATE RECOVER ID
const validateRecoverId = async (recoverId: string): Promise<void> => {
  try {
    await callApi(`user/recover-pwd/${recoverId}`);
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useValidateRecoverId = (
  config: UseMutationOptions<void, AxiosError, string> = {},
): UseMutationResult<void, AxiosError, string> => useMutation(
  validateRecoverId,
  config,
);
// #endregion

// #region RESET PASSWORD
const resetPassword = async ({ newPassword, recoverId }: IResetPassPayload): Promise<void> => {
  try {
    await callApi(
      `user/recover-pwd/${recoverId}`,
      'put',
      { newPassword },
    );

    replace('/');

    Notification.enqueueSnackbar(
      'Password has been changed.',
      'info',
    );
  } catch (error) {
    Notification.enqueueSnackbar(
      showErrorMessage(error),
      'error',
    );

    throw error;
  }
};

export const useResetPassword = (
  config: UseMutationOptions<void, AxiosError, IResetPassPayload> = {},
): UseMutationResult<void, AxiosError, IResetPassPayload> => useMutation(
  resetPassword,
  config,
);
// #endregion
